import React, { useEffect, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { INavigationData } from "us.collection/interfaces";
import Common from "us.common";
import { useTranslation } from "react-i18next";
import { CreditorNotesProps } from "us.collection.creditor/components/Dashboard/Interfaces";
import { ARNoteAction } from "us.collection/actions";
import { RootState } from "us.helper/types";
import { BoxIcons, IconTypes } from "us.icons";
import { EntityType, RegisteredUserType, URLType } from "us.collection/constants";
import { AddNote } from "us.collection/components/Notes/Components";
import { isEditUserNote } from "us.collection/components/Notes/Functions";
import { INoteHistory } from "us.collection/reducers/ARNotes/Interfaces";
import * as Azure from "us.helper/azure";
import dayjs from "dayjs";
import "./CreditorNotes.scss";
import { useLocation } from "react-router-dom";

const { $Skeleton, $Empty, $Popover, $Button, $DateTimeLabel, $Drawer } =
  Common.Components;

/**
 * @description - Component for display creditor notes in creditor dashboard
 * @link CR Task - https://unicorn-solutions.atlassian.net/browse/CCM-2443
 * @param {PropsFromRedux} props
 * @return {JSX.Element}
 * @author Ishan Udyoga <ishanud@unicorn-solutions.com>
 * @since 10/16/2024
 */
const Home: React.FC<CreditorNotesProps & PropsFromRedux> = (props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const {
	getNoteHistory,
    openDrawer,
    creditors,
    metaData,
    summery,
    noteDrawer,
    entityId,
  } = props;

  const auth = new Azure.ADAuth();
  const currentUser: any = auth.currentUser();

  const state = location.state as INavigationData;
  const { refreshCount = 0, currentTab, creditorId } = state ?? {};
  const { isLoading } = metaData;

  const [editNoteContent, setEditNoteContent] = useState({});

  useEffect(() => {
    if (state && currentTab === URLType.CREDITOR && !creditors.isLoading) {
      if (creditorId && getNoteHistory) {
        getNoteHistory({
          entityId: creditorId,
          entityType: EntityType.CREDITOR,
          registeredUserType: RegisteredUserType.ALL,
        });
      }
    }
  }, [creditorId, refreshCount]);

  /**
   * Edit note
   * @param record edit note details
   */
  const editNote = (record: INoteHistory) => {
    setEditNoteContent(record);
    openDrawer &&
      openDrawer({
        title:
          record.registeredUserType === RegisteredUserType.SYSTEM ||
          !isEditUserNote(record, currentUser?.email)
            ? t("US.COLLECTION.CASE:NOTES.VIEW_NOTE")
            : t("US.COLLECTION.CASE:NOTES.EDIT_NOTE"),
        visible: true,
        isEdit: true,
      });
  };

  /**
   * Close drawer
   */
  const onClose = () => {
    openDrawer &&
      openDrawer({
        title: "",
        visible: false,
        isEdit: false,
      });
  };

  return (
    <div className="widget creditor-note-list">
      <div className="widget-header">
        <strong className="d-block mb-2">
          {t("US.COLLECTION.CREDITOR:CREDITORS.CREDITOR_NOTES")}
        </strong>
      </div>
      <div className="widget-body">
        <$Skeleton
          loading={summery?.isLoading || isLoading}
          active
          paragraph={{ rows: 4 }}
        >
          {summery?.data?.length == 0 && (
            <$Empty
              className="mb-4"
              description={`${t("US.COLLECTION.CREDITOR:SETTINGS.NO_DATA")}`}
              image={$Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
          {summery?.data?.map((record: any) => (
            <div className={`cn-item case-log`} key={record?.noteId}>
              <div className="cn-actions">
                <$Popover
                  overlayClassName="history-note-preview"
                  placement="right"
                  title={t("US.COLLECTION.ROUTINESANDACTIVITIES:HISTORY.NOTE")}
                  content={record?.noteText}
                  trigger="hover"
                >
                  <$Button
                    type="link"
                    size="small"
                    className="row-icon row-icon-note"
                  >
                    <BoxIcons type={IconTypes.BOX_ICON} name="case-note" />
                  </$Button>
                </$Popover>
              </div>
              <div className="cn-details">
                <span className="cn-title">
                  <a
                    onClick={() => editNote(record)}
                  >
                    {record?.noteText}
                  </a>
                </span>
                <span className="cn-desc">
                  {dayjs.utc(record?.createdDate).isValid() &&
                    dayjs
                      .utc(record?.createdDate)
                      .isAfter(dayjs.utc("0001-01-01")) && (
                      <$DateTimeLabel value={record?.createdDate} />
                    )}
                  {record?.createrName &&
                    ` | ${record?.createrName}`}
                </span>
              </div>
            </div>
          ))}
        </$Skeleton>
      </div>
      <$Drawer
        title={noteDrawer.title}
        width={"840"}
        open={noteDrawer.visible}
        onClose={() => onClose()}
        destroyOnClose
        placement="left"
      >
        <AddNote
          editNoteContent={editNoteContent}
          entityId={entityId}
          entityType={EntityType.CREDITOR}
        />
      </$Drawer>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  const { common, creditor, domainView, ArNotes } = state;
  const { currentLanguage, currentDateFormat } = common;
  const { summery, noteDrawer } = ArNotes;

  const { creditors } = creditor;
  const { metaData } = domainView;
  return {
    summery,
    creditors,
    metaData,
    noteDrawer,
    currentLanguage,
    currentDateFormat,
  };
};

const { arNoteHistory, addArNote } = ARNoteAction;

const mapDispatchToProps = {
  openDrawer: addArNote.openDrawer,
  getNoteHistory: arNoteHistory.get,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(Home);
